/**
 * PDLC Board — every product's PDLC tasks on one page.
 *
 * The board never calls Asana when it loads. It reads the per-product task list
 * stored on the `pdlc_deliverables` snapshot (#809), which every PDLC dashboard
 * read already writes. Freshness comes from three places: a write from the board
 * patches its own cell, an Asana webhook marks the row stale, and a connected
 * viewer's page load re-reads stale or old rows as that viewer.
 */
import { navFeatureId } from './feature-visibility.js';
/** Nav entry, Feature Visibility switch and API gate share this one id. */
export const PDLC_BOARD_PATH = '/pdlc-board';
export const PDLC_BOARD_FEATURE = navFeatureId(PDLC_BOARD_PATH);
/** The Marketing pipeline reads the same board data; nav entry, visibility switch and API gate share this id. */
export const MARKETING_PIPELINE_PATH = '/marketing/pipeline';
export const MARKETING_PIPELINE_FEATURE = navFeatureId(MARKETING_PIPELINE_PATH);
/**
 * The one rule for "is this a PDLC project", by name: used to find a product's
 * PDLC project in its portfolio, to filter assigned tasks, and by the Asana
 * access sync (#1106). Keep it in one place so they never disagree.
 */
export const isPdlcProjectName = (name) => name.trim().toLowerCase().endsWith('pdlc');
/** True while any dependency is still incomplete. */
export const hasOpenBlocker = (dependencies) => dependencies.some((d) => !d.completed);
/**
 * Not the assignee's turn yet (#1390): a blocker is still open, or the start date is after
 * `today` (YYYY-MM-DD). Home lists these under "Waiting on earlier steps"; the daily email skips them.
 */
export const isPdlcTaskWaiting = (task, today) => hasOpenBlocker(task.dependencies) || (!!task.startOn && task.startOn > today);
/** "1 - NPC Phase" → "P1", "GATE 3 - Executive Sign-Off" → "G3", anything else keeps its name. */
export function shortSection(section) {
    const phase = /^(\d+)\s*-/.exec(section);
    if (phase)
        return `P${phase[1]}`;
    const gate = /^GATE\s*(\d+)/i.exec(section);
    if (gate)
        return `G${gate[1]}`;
    return section;
}
/**
 * The phase a section belongs to (#1390): "N - …" and "GATE N" give N, the Engineering
 * Sample Testing (ADMIN) section gives 2, PLS Sample Testing (ADMIN) gives 4. Anything
 * else is null, and is never judged.
 */
export function pdlcSectionPhase(section) {
    const m = /^(\d+)\s*-/.exec(section) ?? /^GATE\s*(\d+)/i.exec(section);
    if (m)
        return Number(m[1]);
    if (/^Engineering Sample Testing.*\(ADMIN\)/i.test(section))
        return 2;
    if (/^PLS Sample Testing.*\(ADMIN\)/i.test(section))
        return 4;
    return null;
}
/** The highest gate whose every task is completed, or null when none is signed. */
export function highestSignedGate(tasks) {
    const gates = new Map();
    for (const t of tasks) {
        const n = /^GATE\s*(\d+)/i.exec(t.section)?.[1];
        if (n)
            gates.set(Number(n), (gates.get(Number(n)) ?? true) && t.completed);
    }
    const signed = [...gates].filter(([, done]) => done).map(([n]) => n);
    return signed.length ? Math.max(...signed) : null;
}
/** A PDLC project that is a template or staging copy, not a live product. */
export const isStagingPdlcProjectName = (name) => /pdlc/i.test(name) && /template|staging|do not use|^\s*zz\b|^\s*pd\s*\/\s*mk\b/i.test(name);
/** Socket room every open board joins. Payloads carry a productId, never task data. */
export const PDLC_BOARD_ROOM = 'pdlc-board';
/** A row older than this is re-read on the next connected page load, webhook or not. */
export const PDLC_BOARD_MAX_AGE_MS = 6 * 60 * 60 * 1000;
const MILESTONE_FIELD = 'MKTG. KEY CALENDAR MILESTONES';
const CATEGORY_FIELD = 'PD/MKTG - Sub-Project Categories';
const MKTG_STATUS_FIELD = 'MKTG TASK STATUS';
/** A task's MKTG TASK STATUS (#1251), from its custom fields; the name matches case-insensitively. Null when unset. */
export const mktgStatusOf = (fields) => {
    const f = fields?.find((x) => x.name.trim().toLowerCase() === MKTG_STATUS_FIELD.toLowerCase());
    return f?.displayValue?.trim() || f?.selected?.[0]?.name?.trim() || null;
};
/**
 * MKTG TASK STATUS values that say nobody has started (#1263). On prod on 2026-10-20,
 * "READY TO WORK" sat on most tasks whatever their state (196 of them "In progress"
 * in Asana), and "NOT READY" means the task can't start yet. The others seen were
 * WIP, PENDING APPROVAL, CHANGES REQUESTED, APPROVED / READY FOR DEPLOYMENT, ON HOLD,
 * COMPLETED / LAUNCHED / ARCHIVED / SCHEDULED and CANCELLED.
 */
const MKTG_NOT_STARTED = /not started|not ready|ready to work|to do|todo/;
/** The MKTG status when it says work has moved; null when unset or only not-started. */
const movedMktgStatus = (mktgStatus) => {
    const value = mktgStatus?.trim();
    return value && !MKTG_NOT_STARTED.test(value.toLowerCase()) ? value : null;
};
/**
 * What colour a cell is. `today` is a YYYY-MM-DD string in the viewer's zone.
 *
 * - Asana TICKS a dropped task as completed, so "Dropped" must be read from
 *   the status pill before `completed`, or it shows as done.
 * - An assignee says nothing: the PDLC template pre-assigns every task. Only
 *   a status other than "Not started" means someone has picked it up.
 * - Overdue wins over in progress: a late task that has been started is still late.
 */
export function pdlcBoardCellState(task, today) {
    const pill = task.status?.trim().toLowerCase() ?? '';
    if (task.completed)
        return /drop|cancel/.test(pill) ? 'dropped' : 'done';
    if (task.dueOn && task.dueOn < today)
        return 'overdue';
    // An MKTG status that says work has moved wins (#1251). A not-started one says
    // nothing the pill doesn't, so the pill decides (#1263).
    if (movedMktgStatus(task.mktgStatus))
        return 'progress';
    if (pill && pill !== 'not started')
        return 'progress';
    return 'todo';
}
/** The status a board shows for a task: a moved MKTG status while unfinished, else the pill, else the MKTG status. */
export const pdlcBoardStatusLabel = (task) => {
    const mktg = task.completed ? null : task.mktgStatus?.trim() || null;
    return movedMktgStatus(mktg) || task.status || mktg;
};
const fieldValue = (task, name) => task.customFields.find((f) => f.name === name)?.displayValue?.trim() || null;
/** Flatten a dashboard's sections into the board's task list, in Asana order. */
export function toPdlcBoardTasks(sections) {
    return sections.flatMap((section) => section.tasks.map((task) => ({
        gid: task.gid,
        name: task.name,
        section: section.name,
        completed: task.completed,
        dueOn: task.dueOn,
        assigneeName: task.assigneeName,
        status: task.status?.name ?? null,
        mktgStatus: mktgStatusOf(task.customFields),
        permalinkUrl: task.permalinkUrl,
        isMilestone: task.isMilestone,
        blockedBy: task.dependencies.filter((d) => !d.completed).map((d) => d.name),
        dept: task.deptResponsible.map((d) => d.name),
        milestone: fieldValue(task, MILESTONE_FIELD),
        category: fieldValue(task, CATEGORY_FIELD),
    })));
}
/**
 * Webhook events that change what the board draws. Comments, likes and
 * attachments fire far more often and change nothing here, and each stale mark
 * costs a full project re-read, so they are ignored.
 */
const BOARD_TASK_FIELDS = new Set([
    'completed',
    'due_on',
    'due_at',
    'start_on',
    'assignee',
    'name',
    'custom_fields',
    'custom_type_status_option',
    'dependencies',
    'memberships',
]);
export function isPdlcBoardEvent(event) {
    const e = event;
    if (e?.resource?.resource_type !== 'task')
        return false;
    // added / removed / deleted / undeleted all change the column set.
    if (e.action !== 'changed')
        return true;
    return BOARD_TASK_FIELDS.has(e.change?.field ?? '');
}
/**
 * The template's deliverables: tasks whose completion leaves an artifact behind
 * (a document, file, asset or published page), as opposed to gates, approvals,
 * handoffs and coordination. Classified from the 2026-09 "PD/MK ALL BRANDS -
 * PDLC" template. Matched by name, so a renamed template task drops out of the
 * "Deliverables only" filter until it is added here.
 */
export const PDLC_BOARD_DELIVERABLES = new Set([
    'Complete Product Proposal Form',
    'Complete Competitive Matrix',
    'Complete PPS: Product Positioning + The "Why" + Where We Win',
    'Define Fixture DNA',
    'Create Product Walkthrough Video',
    'Finalize Product Name',
    'Create Product Logo / Wordmark',
    'Create (PRIVATE) Github Repository',
    'OUTERBOX DESIGN',
    'Create 3D Product Renders',
    'Create Application Renders (If Required)',
    'Create Retail Color Box (Retail Only)',
    'Create Product Page URL',
    'Create Product QR Code',
    'Create QRG',
    'Finalize Product Genesis Document',
    'Create LMS Project (If Required)',
    'Create Commercialization Plan (A Launch)',
    'Create Product Description',
    'Build Preliminary Product Page & Print PDF (Cutsheet)',
    'Generate Preliminary Cutsheet',
    'Upload Preliminary DAM Assets',
    'Complete FR Translations',
    'Complete DE Translations',
    'Complete ES Translations',
    'Upload Product Page Translations',
    'Define PLS Testing Plan',
    'Finalize GDTF File',
    'Distribute Final GDTF File',
    'Complete PLS Testing',
    'Complete BOM List',
    'Upload IES Files',
    'Upload Photometrics Report',
    'Create DMX Chart',
    'Upload Approved Certifications & Reports',
    'Upload User Manual',
    'Upload Final Specs and Build Info',
    'Publish LMS Training',
    'Prepare Internal Company-Wide Go-Live Email',
    'Publish Service Manual, Parts etc.',
    'Publish Github Repository',
    'Prepare Internal Campaign Alert',
    'Publish Product Page (GO LIVE)',
    'Finalize DAM Assets',
]);
/** Asana task names carry stray trailing spaces ("Define Fixture DNA "). */
export const isPdlcBoardDeliverable = (name) => PDLC_BOARD_DELIVERABLES.has(name.trim());
/**
 * Letters and digits only, accents dropped: "PD → MKTG Handoff" and
 * "PD-MKTG Handoff", or "Define Fixture DNA " with its stray space, are one task.
 */
export const normalizeTaskName = (name) => name.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]/g, '');
/**
 * The PD and TW tasks marketing waits on (#1107): the Marketing pipeline shows
 * them beside the MKTG tasks, and launch setup (#1111) multi-homes them into Go Live.
 */
export const MARKETING_HANDOFF_TASK_NAMES = [
    'Release CAD/STP Files for Rendering',
    'Finalize GDTF File',
    'PLS Approval / Sign-Off',
    'Upload User Manual',
    'Upload Photometrics Report',
];
/**
 * Renames between PDLC template versions, old name → current name. "Current"
 * is the latest template, Asana project 1217490060901619: every target here
 * must match one of its task names. Asana keeps
 * no link from a project task to its template task, so name is the only join.
 * Only clear one-to-one renames belong here; a step that has no equivalent in
 * the current template (the old product-naming sub-steps, "DAM Upload", which
 * could be preliminary or final) keeps its own column rather than being guessed.
 * DJ and Pro are moving to separate templates: map an old name to its successor
 * in the SAME line's template, never a DJ step onto a Pro one.
 */
export const PDLC_TEMPLATE_RENAMES = {
    // NPC phase, pre-2026 template (e.g. "COLORado Panel 4: PD Cycle PDLC").
    'Competitive Matrix': 'Complete Competitive Matrix',
    'PPS: Product Positioning + The "Why" + Where We Win': 'Complete PPS: Product Positioning + The "Why" + Where We Win',
    'Product Naming Suggestions': 'Propose Product Name Suggestions',
    'Launch Type (A.B or C - V2 Replacement) - proposed': 'Propose Launch Type (A, B C, or D)',
    'Target Launch Date': 'Propose Target Launch Date',
    'Fixture DNA': 'Define Fixture DNA',
    'Initial Drawing Received': 'Review Initial Product Drawings',
    'Initial Drawings Received': 'Review Initial Product Drawings',
    'Initial Drawing & Industrial Design Approved': 'Approve Industrial Design Drawing',
    'Brand Review: Industrial Design': 'Review Industrial Design',
    'NPC MEETING': 'Conduct NPC MEETING Presentation',
    // Development phase, same pre-2026 template. The old "Launch Type … Confirmed"
    // and "Confirm Target Launch Date" are NOT mapped: the current template merges
    // them into one "Finalize Target Launch Date/Type", and a row keyed twice
    // would silently lose one task.
    'Supplier Specs - CHANGE - Supplier spec, menu map, dmx chart, chamsys profiles received': 'Receive Initial Supplier Documentation',
    'Engineering Sample Arrival': 'Receive Engineering Sample',
    'Assigng Engineer for Sample Arrival - CHANGE Spelling error': 'Assign Engineering Sample Testing',
    'Engineering Sample Approval': 'Approve Engineering Sample',
    'CAD / STP files - Received': 'Receive CAD / STP files',
    'Send Finalized CAD/STP Files for Rendering': 'Release CAD/STP Files for Rendering',
    // The two earlier templates' names for the rest of the latest template.
    // Checked against the live board 2026-09-24: every old name occurs, and no
    // product has both an old name and its target.
    'Initial Drawings / Industrial Design Approved': 'Approve Industrial Design Drawing',
    'Industrial Design Approved': 'Approve Industrial Design Drawing',
    'Initial Supplier Documents Received': 'Receive Initial Supplier Documentation',
    'Assign Engineer for Sample Arrival': 'Assign Engineering Sample Testing',
    'PM Initial Walkthrough - Video': 'Create Product Walkthrough Video',
    'PM Initial Walkthrough': 'Create Product Walkthrough Video',
    'Confirm Target Launch Date/Type': 'Finalize Target Launch Date/Type',
    'Finalize Launch Type': 'Finalize Target Launch Date/Type',
    'Preliminary DAM Upload': 'Upload Preliminary DAM Assets',
    // The PRELIMINARY page review (David, 2026-09-24), not the final one.
    'Product Page Review - Sign Off': 'Approve Preliminary Product Page',
    'Finalize Product Naming': 'Finalize Product Name',
    'Application Renders (If Needed)': 'Create Application Renders (If Required)',
    'Send QRG & OEM to Factory': 'Release QRG & OEM to Factory',
    'Send QRG to Factory': 'Release QRG & OEM to Factory',
    'Commercialization Plan by MKTG (A Launch)': 'Create Commercialization Plan (A Launch)',
    'Commercialization Plan by MKTG': 'Create Commercialization Plan (A Launch)',
    'Product Description Review by Brand': 'Approve Product Description',
    'Product Page Translations': 'Upload Product Page Translations',
    'PLS Sample Arrival - (Sales, Marketing & Product - PLS)': 'Receive PLS Sample',
    'MKTG Sample and Global In Stock Arrival - ETA': 'Confirm MKTG Sample and Global In Stock - ETAs',
    'Assign Engineer for PLS Testing': 'Assign PLS Testing',
    'PLS Testing Plan': 'Define PLS Testing Plan',
    'Update GDTF File': 'Finalize GDTF File',
    'PLS Testing Complete': 'Complete PLS Testing',
    'BOM List': 'Complete BOM List',
    'Inform Tech Support': 'Enable Tech Support',
    'Gather Certifications & Reports': 'Gather Certifications & Compliance Reports',
    'Redact and Publish Certifications & Reports': 'Upload Approved Certifications & Reports',
    'Send Initial Firmware to TW': 'Release Initial Firmware to TW',
    'Demo Showfile (a) - confirmed': 'Approve Demo Showfile (A Launch)',
    'In-field activations': 'Identify In-field activations',
    'Final Specs and Build Info': 'Upload Final Specs and Build Info',
    'Final Specs': 'Upload Final Specs and Build Info',
    'Final Product Page Review': 'Complete Final Product Page Review',
    'Final FR Review': 'Approve Final FR Page',
    'Final DE Review': 'Approve Final DE Page',
    'Final ES Review': 'Approve Final ES Page',
    'Internal Go-Live Email (company-wide)': 'Prepare Internal Company-Wide Go-Live Email',
    'Partner Enablement': 'Complete Partner Enablement',
    'Service Manual, Parts etc.': 'Publish Service Manual, Parts etc.',
    'Create 3D Renders': 'Create 3D Product Renders',
    'Create Application Renders': 'Create Application Renders (If Required)',
    'Create Logo': 'Create Product Logo / Wordmark',
    'Create QR Code': 'Create Product QR Code',
    'Create Preliminary Cutsheet': 'Generate Preliminary Cutsheet',
    'Print/Save Preliminary Cutsheet from Product Page': 'Generate Preliminary Cutsheet',
    'Initial Product Page Creation': 'Build Preliminary Product Page & Print PDF (Cutsheet)',
    'Initial Product Page Creation and Print PDF': 'Build Preliminary Product Page & Print PDF (Cutsheet)',
    'Build Preliminary Product Page & Print PDF': 'Build Preliminary Product Page & Print PDF (Cutsheet)',
    'IES Files': 'Upload IES Files',
    'Photometrics Report': 'Upload Photometrics Report',
    'Create DMX Chart (PRO)': 'Create DMX Chart',
    'Create User Manual': 'Upload User Manual',
    'Create Github Repository (PRIVATE)': 'Create (PRIVATE) Github Repository',
    'Make Github Repository Public': 'Publish Github Repository',
    'Redact and Publish Certifications': 'Upload Approved Certifications & Reports',
    'CAD File TW': 'Prepare CAD Files for Tech Writing',
    'FR Translations': 'Complete FR Translations',
    'DE Translations': 'Complete DE Translations',
    'ES Translations': 'Complete ES Translations',
    'Design Colorbox': 'Create Retail Color Box (Retail Only)',
    'COLORBOX Design': 'Create Retail Color Box (Retail Only)',
    'ColorBox Design (If DJ Product)': 'Create Retail Color Box (Retail Only)',
    'Create Color Box': 'Create Retail Color Box (Retail Only)',
    'PCF DOCUMENT SIGN OFF': 'Complete PCF SIGN OFF',
    'PCF SIGN OFF': 'Complete PCF SIGN OFF',
    'NPSO DOCUMENT SIGN OFF': 'Complete NPSO DOCUMENT SIGN OFF',
    'PLS Approved': 'PLS Approval / Sign-Off',
    'PLS Approval': 'PLS Approval / Sign-Off',
    'Product Page Live': 'Publish Product Page (GO LIVE)',
    'Receive GDTF File': 'Receive Initial GDTF File',
    'RECIEVE GDTF from Factory': 'Receive Initial GDTF File',
    'PLS Sample Arrival': 'Receive PLS Sample',
    'Testing Plan': 'Define PLS Testing Plan',
    'Demo Showfile - confirmed': 'Approve Demo Showfile (A Launch)',
    'Final Product Name': 'Finalize Product Name',
    'Create LMS Project (If Needed)': 'Create LMS Project (If Required)',
    'Create LMS Project (A/B Launches Only if Needed)': 'Create LMS Project (If Required)',
    // PD/MK PRO template (#1084): its P5 "Finalize Genesis Doc" is the same
    // finalise step. Its P3 "Create Product Genesis Document" is its own column.
    'Finalize Genesis Doc': 'Finalize Product Genesis Document',
};
//# sourceMappingURL=pdlc-board.js.map